import { useEventListener } from "@vueuse/core";
import { computed, onMounted, ref, toValue } from "vue";

/**
 * Bind to a native <select> by id, mirror its options/value into refs, and
 * provide an apply() that writes back to the source select and dispatches a
 * bubbling change event on its form.
 *
 * @param {import("vue").MaybeRefOrGetter<string>} selectIdRef
 */
export function useSortSelect(selectIdRef) {
  const sourceSelect = ref(null);
  const options = ref([]);
  const selectedValue = ref("");

  const isBound = computed(() => Boolean(sourceSelect.value));

  function findSourceSelect() {
    return document.getElementById(toValue(selectIdRef));
  }

  function sync() {
    const source = sourceSelect.value;
    if (!source) {
      options.value = [];
      selectedValue.value = "";
      return;
    }

    const allOptions = Array.from(source.options).map((option) => ({
      label: option.textContent?.trim() || "",
      value: option.value,
      hint: option.dataset.sortHint || "",
    }));
    options.value = allOptions;

    const defaultValue = options.value[0]?.value || "";
    selectedValue.value = source.value || defaultValue;
  }

  function apply(value) {
    const source = sourceSelect.value;
    if (!source || !value) {
      return false;
    }

    if (source.value !== value) {
      source.value = value;
      selectedValue.value = value;
      // Dispatch a bubbling change event on the form so EmbeddedIndexCollectionSearch
      // routes through handleFilterChange (results-only refresh) and preserves the
      // sort wrapper across the AJAX update, matching doctor finder behavior.
      source.form?.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    return false;
  }

  useEventListener(sourceSelect, "change", sync);

  onMounted(() => {
    sourceSelect.value = findSourceSelect();
    sync();
  });

  return { sourceSelect, options, selectedValue, isBound, apply, sync };
}
